import { TreeList, Button, Spinner } from '@ibsys/kit';
import { DragDropProvider, DragOverlay, type DragEndEvent, type DragDropManager, PointerSensor } from '@dnd-kit/react';
import { PointerActivationConstraints } from '@dnd-kit/dom';
import { isSortable } from '@dnd-kit/react/sortable';
import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { move } from '@dnd-kit/helpers';
import { ArrowRightIcon, ClipboardListIcon, ChevronsDownIcon, GripVerticalIcon, PackageIcon, PlusIcon } from 'lucide-react';
import { FC, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  useExpertReport,
  useReorderExpertReportBlocks,
  expertReportKeys,
  flattenBlocksOrder,
  type ExpertReportResponse,
} from '@/entities/expert-report';
import { useHasPermission } from '@/entities/profile';
import { cn } from '@/shared/lib';
import { ExpertReportTreeNode } from './ExpertReportTreeNode';
import { DropIndicator } from './DropIndicator';
import {
  buildSortableGroups,
  collectSubtreeBlockIds,
  computeDropBand,
  getBlocksOrderSignature,
  moveBlockInTree,
  parseBlockNodeId,
  rebuildBlocksFromGroups,
  reportWithBlocksToTreeNode,
  type TreeNodeData,
  type TreeNodeType,
} from './treeUtils';

/**
 * Откладывает применение оптимистичного дерева до фактического завершения перетаскивания
 * (статус операции стал idle). Нужен при переносе блока в другой контейнер: если применить новое
 * дерево синхронно в момент отпускания, React переносит реальный узел в другой контейнер, пока
 * dnd-kit ещё доигрывает завершение, и в исходном блоке остаётся неактивная фантомная копия.
 * Ограничение по кадрам — страховка, чтобы применение не зависло, если idle почему-то не наступит.
 */
const scheduleAfterDrop = (manager: DragDropManager, apply: () => void) => {
  let frames = 0;
  const run = () => {
    if (manager.dragOperation.status.idle || frames > 30) {
      apply();
      return;
    }
    frames += 1;
    requestAnimationFrame(run);
  };
  requestAnimationFrame(run);
};

type Props = {
  reportId: number;
  reportName: string;
  isExpanded: boolean;
  /** Все раскрытые узлы дерева — по ним определяются свёрнутые блоки для авто-разворачивания при drag */
  expandedIds: string[];
  activeId: string;
  isAddingBlock?: boolean;
  onSelect: (nodeId: string, type: TreeNodeType) => void;
  onAddBlock: (reportId: number, parentBlockId?: number) => void;
  onExpandBlocks: (ids: string[]) => void;
  isDesktop?: boolean;
};

const ICON_SIZE = 18;

export const ExpertReportTreeNodeWithData: FC<Props> = ({
  reportId,
  reportName,
  isExpanded,
  expandedIds,
  activeId,
  isAddingBlock,
  onSelect,
  onAddBlock,
  onExpandBlocks,
  isDesktop,
}) => {
  const { t } = useTranslation('expertReport');
  const queryClient = useQueryClient();
  const allowAdmin = useHasPermission('ADMIN_EXPERT_REPORT');
  const reorderBlocksMutation = useReorderExpertReportBlocks();
  const { data: reportDetails, isLoading: isLoadingDetails } = useExpertReport(isExpanded ? reportId : undefined);

  // Снимок состояния отчёта на момент начала перетаскивания — для отката при ошибке сохранения.
  const dragSnapshotRef = useRef<ExpertReportResponse | null>(null);

  // Контейнер дерева блоков — относительно него единый DropIndicator позиционирует индикатор вставки.
  const blockTreeRef = useRef<HTMLDivElement>(null);

  const nodeId = `report-${reportId}`;
  const isActive = activeId === nodeId;

  const treeNode: TreeNodeData = useMemo(() => {
    const base = {
      id: nodeId,
      label: reportName,
      type: 'report' as const,
      reportId,
    };

    if (!isExpanded) return { ...base, children: undefined };
    if (isLoadingDetails) return { ...base, children: [] };
    if (reportDetails) {
      return reportWithBlocksToTreeNode({ id: reportId, name: reportName, isDeleted: false }, reportDetails.blocks);
    }
    return { ...base, children: [] };
  }, [nodeId, reportId, reportName, isExpanded, isLoadingDetails, reportDetails]);

  /* На десктопе (контейнер >= 1280px) клик по заголовку группы открывает карточку.
     На мобильном клик только раскрывает аккордеон — навигация через rightSlot-кнопку. */
  const handleRowClick = () => {
    if (isDesktop) {
      onSelect(nodeId, 'report');
    }
  };

  const handleAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddBlock(reportId);
  };

  const handleExpandAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Собираем ID всех раскрываемых групп из текущего дерева блоков
    const ids: string[] = [String(reportId)];
    const collect = (nodes: TreeNodeData[]) => {
      for (const node of nodes) {
        if (node.children?.length) {
          ids.push(node.id);
          collect(node.children);
        }
      }
    };
    collect(treeNode.children ?? []);
    onExpandBlocks(ids);
  };

  // Сохраняет текущий (уже оптимистично применённый) порядок блоков на сервере лёгким методом
  // перестановки: тело — только позиции и родители существующих неудалённых блоков, без правил,
  // названия и предиката. Успех подтверждает кеш ответом сервера внутри useReorderExpertReportBlocks
  // (без рефетча detail, чтобы не ремоунтить дерево под drop-анимацией); здесь — только откат при ошибке.
  const persistReorder = (report: ExpertReportResponse, snapshot: ExpertReportResponse) => {
    reorderBlocksMutation.mutate(
      {
        id: reportId,
        body: { blocks: flattenBlocksOrder(report.blocks) },
      },
      {
        onError: () => {
          queryClient.setQueryData(expertReportKeys.detail(reportId), snapshot);
        },
      },
    );
  };

  // Запоминаем исходное состояние, чтобы откатиться при ошибке сохранения.
  const handleDragStart = () => {
    dragSnapshotRef.current =
      queryClient.getQueryData<ExpertReportResponse>(expertReportKeys.detail(reportId)) ?? reportDetails ?? null;
  };

  /**
   * Завершение перетаскивания. Во время перетаскивания дерево не трогаем — живую анимацию ведёт
   * сама библиотека (как в дереве компетенций и списке правил), поэтому копии блока и неактивные
   * блоки не возникают. Итоговый порядок вычисляем один раз здесь: вложение под лист (середина строки
   * листовой цели) — прямой перенос под цель, остальное — хелпером `move` (перестановка на уровне и
   * смена уровня). Перенос между контейнерами (смена родителя) применяем не синхронно, а после
   * завершения операции, иначе в исходном блоке остаётся фантомная неактивная копия.
   */
  const handleDragEnd = (event: DragEndEvent, manager: DragDropManager) => {
    const snapshot = dragSnapshotRef.current;
    dragSnapshotRef.current = null;
    if (event.canceled || !snapshot) return;

    const { source, target } = event.operation;
    if (!source || !isSortable(source) || !target) return;

    const cached =
      queryClient.getQueryData<ExpertReportResponse>(expertReportKeys.detail(reportId)) ?? snapshot;
    const draggedBlockId = parseBlockNodeId(String(source.id));
    if (draggedBlockId === null) return;

    const sourceNodeId = String(source.id);
    const targetBlockId = parseBlockNodeId(String(target.id));
    const groups = buildSortableGroups(cached.blocks, reportId);

    // Применяет вычисленный оптимистичный результат. Смену родителя (перенос в другой контейнер)
    // откладываем до завершения перетаскивания — см. scheduleAfterDrop; перестановку на том же
    // уровне применяем сразу, чтобы не терять плавность.
    const applyResult = (optimisticReport: ExpertReportResponse, isReparent: boolean) => {
      const commit = () => {
        queryClient.setQueryData(expertReportKeys.detail(reportId), optimisticReport);
        persistReorder(optimisticReport, snapshot);
      };
      if (isReparent) scheduleAfterDrop(manager, commit);
      else commit();
    };

    // Вложение под лист: середина строки листовой цели. Лист — блок без активных дочерних блоков.
    const isTargetLeaf = targetBlockId !== null && !(groups[`block-${reportId}-${targetBlockId}`]?.length);
    if (isTargetLeaf && targetBlockId !== null) {
      const rect = manager.dragOperation.target?.shape?.boundingRectangle;
      const pointerY = manager.dragOperation.position.current?.y;
      const band = rect && pointerY != null ? computeDropBand(pointerY, rect.top, rect.height, true) : null;
      if (band === 'nest') {
        const descendants = collectSubtreeBlockIds(cached.blocks, draggedBlockId);
        if (targetBlockId === draggedBlockId || descendants.has(targetBlockId)) return;

        const optimisticReport: ExpertReportResponse = {
          ...cached,
          blocks: moveBlockInTree(cached.blocks, draggedBlockId, targetBlockId, 0),
        };
        applyResult(optimisticReport, true); // вложение под другой блок — всегда смена родителя
        return;
      }
    }

    // Перестановка/перемещение сортируемых блоков — итоговое дерево из одного применения move.
    const nextGroups = move(groups, event);
    if (nextGroups === groups) return; // позиция не изменилась — запрос не шлём

    // Запрет вложения блока в собственное поддерево (защита от цикла).
    const newParentGroupId = Object.keys(nextGroups).find((groupId) => nextGroups[groupId].includes(sourceNodeId));
    const newParentBlockId = newParentGroupId ? parseBlockNodeId(newParentGroupId) : null;
    if (newParentBlockId !== null) {
      const descendants = collectSubtreeBlockIds(cached.blocks, draggedBlockId);
      if (newParentBlockId === draggedBlockId || descendants.has(newParentBlockId)) return;
    }

    const newBlocks = rebuildBlocksFromGroups(cached.blocks, nextGroups, reportId);
    if (getBlocksOrderSignature(newBlocks) === getBlocksOrderSignature(cached.blocks)) return; // холостая перестановка

    // Сменился ли контейнер-родитель перетаскиваемого блока — от этого зависит, откладывать ли применение.
    const oldParentGroupId = Object.keys(groups).find((groupId) => groups[groupId].includes(sourceNodeId));
    const isReparent = newParentGroupId !== oldParentGroupId;

    const optimisticReport: ExpertReportResponse = { ...cached, blocks: newBlocks };
    applyResult(optimisticReport, isReparent);
  };

  const hasChildren = treeNode.children && treeNode.children.length > 0;
  const blockChildren = useMemo(() => (treeNode.children ?? []).filter((c) => c.type === 'block'), [treeNode.children]);

  // Плоское соответствие nodeId → название для плавающей копии (DragOverlay): она рисуется поверх
  // дерева, поэтому подпись перетаскиваемого блока нужно находить по его id.
  const blockLabelByNodeId = useMemo(() => {
    const map = new Map<string, string>();
    const walk = (nodes: TreeNodeData[]) => {
      for (const node of nodes) {
        map.set(node.id, node.label);
        if (node.children?.length) walk(node.children);
      }
    };
    walk(treeNode.children ?? []);
    return map;
  }, [treeNode.children]);

  // nodeId листовых блоков (без активных дочерних блоков): только они допускают вложение внутрь по
  // середине строки. Единый DropIndicator по этому набору выбирает зону строки-цели.
  const leafNodeIds = useMemo(() => {
    const set = new Set<string>();
    const walk = (nodes: TreeNodeData[]) => {
      for (const node of nodes) {
        if (node.type === 'block' && !(node.children && node.children.length)) set.add(node.id);
        if (node.children?.length) walk(node.children);
      }
    };
    walk(treeNode.children ?? []);
    return set;
  }, [treeNode.children]);

  // nodeId свёрнутых блоков с вложенными блоками: при задержке наведения перетаскиваемого блока такой
  // блок авто-разворачивается, открывая дочерние блоки как цели вложения/вставки (spring-loaded).
  const expandedIdSet = useMemo(() => new Set(expandedIds), [expandedIds]);
  const collapsedGroupIds = useMemo(() => {
    const set = new Set<string>();
    const walk = (nodes: TreeNodeData[]) => {
      for (const node of nodes) {
        if (node.type === 'block' && node.children?.length && !expandedIdSet.has(node.id)) set.add(node.id);
        if (node.children?.length) walk(node.children);
      }
    };
    walk(treeNode.children ?? []);
    return set;
  }, [treeNode.children, expandedIdSet]);

  // Соответствие nodeId → множество nodeId его потомков — чтобы не авто-разворачивать собственное
  // поддерево перетаскиваемого блока (вложение туда запрещено).
  const descendantsByNodeId = useMemo(() => {
    const map = new Map<string, Set<string>>();
    const walk = (nodes: TreeNodeData[]): Set<string> => {
      const subtree = new Set<string>();
      for (const node of nodes) {
        const childIds = node.children?.length ? walk(node.children) : new Set<string>();
        map.set(node.id, childIds);
        subtree.add(node.id);
        for (const id of childIds) subtree.add(id);
      }
      return subtree;
    };
    walk(treeNode.children ?? []);
    return map;
  }, [treeNode.children]);

  // Разворачивает свёрнутый блок-цель по задержке наведения (вызывается из подписки DropIndicator).
  const handleAutoExpand = useCallback((nodeId: string) => onExpandBlocks([nodeId]), [onExpandBlocks]);

  return (
    <TreeList.Group
      value={String(reportId)}
      classNames={{ header: 'group/report' }}
      label={
        <div className="flex flex-1 items-start gap-2">
          <div
            className={cn(
              'mt-0.5 shrink-0',
              isActive ? 'text-accent-foreground' : 'text-muted-foreground group-hover:text-foreground',
            )}
          >
            <ClipboardListIcon size={ICON_SIZE} />
          </div>
          <span className="line-clamp-2 flex-1 text-left text-sm font-medium leading-tight">{reportName}</span>
        </div>
      }
      onClick={handleRowClick}
      rightSlot={
        isAddingBlock ? (
          <Spinner className="ml-auto size-4 shrink-0" />
        ) : (
          <div className="flex shrink-0 items-center gap-0.5">
            {/* Кнопка перехода к карточке — только когда контейнер не десктопный */}
            {!isDesktop && (
              <Button
                aria-label={t('openCard')}
                variant="ghost"
                size="icon"
                className="size-6"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(nodeId, 'report');
                }}
              >
                <ArrowRightIcon className="size-3.5" />
              </Button>
            )}
            {/* Кнопки управления — только на десктопе, появляются по hover */}
            {isDesktop && (
              <div className="flex gap-0.5 opacity-0 transition-opacity group-hover/report:opacity-100">
                <Button
                  aria-label={t('expandAll')}
                  variant="ghost"
                  size="icon"
                  className="size-6"
                  onClick={handleExpandAll}
                  tooltip={t('expandAll')}
                >
                  <ChevronsDownIcon className="size-3.5" />
                </Button>
                {allowAdmin && (
                  <Button
                    aria-label={t('addBlock')}
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    onClick={handleAdd}
                    tooltip={t('addBlock')}
                  >
                    <PlusIcon className="size-3.5" />
                  </Button>
                )}
              </div>
            )}
          </div>
        )
      }
    >
      <div className="relative" ref={blockTreeRef}>
        {/* Состояние загрузки */}
        {isExpanded && isLoadingDetails && (
          <div className="flex items-center gap-2">
            <Spinner className="size-4" />
            <span className="text-xs text-muted-foreground">{t('loading')}</span>
          </div>
        )}

        {/*
         * Единый DragDropProvider для всего дерева блоков отчёта.
         * Каждый блок сортируется с group=id_родителя, что заменяет вложенные SortableContext.
         */}
        {isExpanded && !isLoadingDetails && hasChildren && (
          <DragDropProvider
            sensors={[PointerSensor.configure({ activationConstraints: [new PointerActivationConstraints.Distance({ value: 5 })] })]}
            modifiers={[RestrictToVerticalAxis]}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            {blockChildren.map((child, blockIndex) => (
              <ExpertReportTreeNode
                key={child.id}
                node={child}
                index={blockIndex}
                groupId={nodeId}
                level={1}
                activeId={activeId}
                onSelect={onSelect}
                onAddBlock={onAddBlock}
                onExpandBlock={(id) => onExpandBlocks([id])}
                isDesktop={isDesktop}
              />
            ))}
            {/* Плавающая копия перетаскиваемого блока: следует за курсором вместо переноса реальных
                узлов дерева, поэтому во время перетаскивания уровни не меняются в DOM. Копию делаем
                полупрозрачной: библиотека рисует её поверх дерева с максимальным z-index, и сквозь
                неё виден индикатор места вставки под целевой строкой (иначе копия его перекрывает).
                Drop-анимацию отключаем: при переносе между блоками дерево применяется отложенно, и
                «отлёт» копии к прежнему месту выглядел бы как возврат блока перед его перескоком. */}
            <DragOverlay dropAnimation={null}>
              {(source) => {
                const label = blockLabelByNodeId.get(String(source.id));
                if (!label) return null;
                return (
                  <div className="flex items-center gap-2 rounded-lg border border-primary/40 bg-card/60 px-3 py-2 shadow-sm ring-1 ring-border">
                    <PackageIcon size={14} className="shrink-0 text-muted-foreground" />
                    <span className="text-sm font-medium">{label}</span>
                    <GripVerticalIcon size={14} className="ml-auto shrink-0 text-muted-foreground" />
                  </div>
                );
              }}
            </DragOverlay>
            {/* Единый индикатор места вставки и авто-разворачивание свёрнутых блоков: одна подписка
                на движение указателя на всё дерево. */}
            <DropIndicator
              containerRef={blockTreeRef}
              leafNodeIds={leafNodeIds}
              collapsedGroupIds={collapsedGroupIds}
              descendantsByNodeId={descendantsByNodeId}
              onAutoExpand={handleAutoExpand}
            />
          </DragDropProvider>
        )}

        {/* Пустое состояние */}
        {isExpanded && !isLoadingDetails && !hasChildren && (
          <div className="select-none text-xs italic text-muted-foreground">{t('emptyNode')}</div>
        )}
      </div>
    </TreeList.Group>
  );
};
